import {
  call,
  fork,
  put,
  take,
  takeEvery,
  all,
  select,
} from 'redux-saga/effects';
import { GoogleAuthProvider, getAdditionalUserInfo } from 'firebase/auth';
import { serverTimestamp } from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import types from './actionTypes';
import * as authActions from './actions';

import * as usersActions from '../users/actions';
import * as dashboardActions from '../dashboard/actions';
import * as dailyFacebookCampaignInsightsActions from '../facebookAd/dailyCampaignInsights/actions';
import * as dailyGoogleCampaignInsightsActions from '../googleAd/dailyCampaignInsights/actions';
import * as leadDailyInsightsActions from '../leads/dailyInsights/actions';
import * as leadsActions from '../leads/actions';
import * as peopleActions from '../people/actions';
import * as audiencesActions from '../audiences/actions';
import * as customersActions from '../customers/actions';
import * as dailyCustomerInsightsActions from '../customers/dailyInsights/actions';
import * as acquisitionOrdersActions from '../acquisitionOrders/actions';
import * as monetizationOrdersActions from '../monetizationOrders/actions';
import * as aiAgentsActions from '../aiAgents/actions';
import * as aiConversationsActions from '../aiConversations/actions';
import * as aiConversationMonitorActions from '../aiConversationMonitor/actions';
import * as aiSimulationActions from '../aiSimulation/actions';
import * as dailyMonetizationOrderInsightsActions from '../monetizationOrders/dailyInsights/actions';
import * as monetizationItemsActions from '../monetizationItems/actions';
import * as leadFieldsActions from '../leadFields/actions';
import * as leadFormsActions from '../leadForms/actions';
import * as campaignsActions from '../campaigns/actions';
import * as dailyCampaignInsightsActions from '../campaigns/dailyInsights/actions';
import * as surveysActions from '../surveys/actions';
import * as surveyFormsActions from '../surveyForms/actions';
import * as messageTemplatesActions from '../messageTemplates/actions';
import * as smsMessagesActions from '../smsMessages/actions';
import * as pushNotificationsActions from '../pushNotifications/actions';
import * as callsActions from '../calls/actions';
import * as callsInsightsActions from '../callInsights/actions';
import * as nextCallActions from '../nextCall/actions';
import * as suppliersActions from '../suppliers/actions';
import * as dailySaleInsightsActions from '../suppliers/dailySaleInsights/actions';
import * as dailyPublisherInsightsActions from '../suppliers/dailyPublisherInsights/actions';
import * as countriesActions from '../countries/actions';
import * as dailyCountriesInsightsActions from '../countries/dailyInsights/actions';

import { toDateFirebase } from '../../helpers/sharedFunction';
import toastr from 'toastr';
import roles from '../../config/roles';

const authGoogleProvider = new GoogleAuthProvider();
authGoogleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
authGoogleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');

function* loginWithGoogleSaga({ history }) {
  try {
    const result = yield call(rsf.auth.signInWithPopup, authGoogleProvider);
    const { isNewUser } = getAdditionalUserInfo(result);
    isNewUser
      ? yield call(createAdminSaga, result.user, 'google')
      : yield call(fetchAdminSaga, result.user.uid, history);
  } catch (error) {
    yield put(authActions.loginWithGoogleFailure(error));
  }
}

function* loginWithEmailSaga({ email, password, history }) {
  try {
    const { user } = yield call(
      rsf.auth.signInWithEmailAndPassword,
      email,
      password,
    );
    yield call(fetchAdminSaga, user.uid, history);
  } catch (error) {
    yield put(authActions.loginWithEmailFailure(error));
  }
}

function* registerWithEmailSaga({ email, password, displayName }) {
  try {
    const { user } = yield call(
      rsf.auth.createUserWithEmailAndPassword,
      email,
      password,
    );
    yield call(createAdminSaga, { ...user, displayName }, 'email');
  } catch (error) {
    yield put(authActions.registerWithEmailFailure(error));
  }
}

function* logoutSaga({ history }) {
  try {
    yield put(usersActions.resetState());
    yield put(dashboardActions.resetState());
    yield put(dailyFacebookCampaignInsightsActions.resetState());
    yield put(dailyGoogleCampaignInsightsActions.resetState());
    yield put(leadDailyInsightsActions.resetState());
    yield put(leadsActions.resetState());
    yield put(peopleActions.resetState());
    yield put(audiencesActions.resetState());
    yield put(customersActions.resetState());
    yield put(dailyCustomerInsightsActions.resetState());
    yield put(acquisitionOrdersActions.resetState());
    yield put(monetizationOrdersActions.resetState());
    yield put(dailyMonetizationOrderInsightsActions.resetState());
    yield put(monetizationItemsActions.resetState());
    yield put(leadFieldsActions.resetState());
    yield put(leadFormsActions.resetState());
    yield put(aiAgentsActions.resetState());
    yield put(aiConversationsActions.resetState());
    yield put(aiConversationMonitorActions.resetState());
    yield put(aiSimulationActions.resetState());
    yield put(campaignsActions.resetState());
    yield put(dailyCampaignInsightsActions.resetState());
    yield put(surveysActions.resetState());
    yield put(surveyFormsActions.resetState());
    yield put(messageTemplatesActions.resetState());
    yield put(smsMessagesActions.resetState());
    yield put(pushNotificationsActions.resetState());
    yield put(callsActions.resetState());
    yield put(callsInsightsActions.resetState());
    yield put(nextCallActions.resetState());
    yield put(suppliersActions.resetState());
    yield put(dailySaleInsightsActions.resetState());
    yield put(dailyPublisherInsightsActions.resetState());
    yield put(countriesActions.resetState());
    yield put(dailyCountriesInsightsActions.resetState());

    yield call(rsf.auth.signOut);
    yield put(authActions.logoutSuccess());
    history.push('/login');
  } catch (error) {
    yield put(authActions.logoutFailure(error));
  }
}

function* syncAdminSaga() {
  const channel = yield call(rsf.auth.channel);

  while (true) {
    const { error, user } = yield take(channel);

    if (user) {
      yield put(authActions.syncAdmin(user));
    } else {
      yield put(authActions.syncAdmin(null));
    }
  }
}

function* fetchAdminSaga(userId, history) {
  try {
    yield put(countriesActions.syncCountries());

    const adminSnap = yield call(rsf.firestore.getDocument, `users/${userId}`);
    const data = adminSnap.data();

    const admin = {
      id: userId,
      ...data,
      createdAt: toDateFirebase(adminSnap, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(adminSnap, data, 'updatedAt').toDate(),
      }),
    };
    if (admin.role && admin.active) {
      yield put(authActions.fetchAdminSuccess(admin, ''));
      //If this user manage only one country then set countryId value
      if (admin.countryIds && admin.countryIds.length === 1)
        yield put(dashboardActions.setCountry(admin.countryIds[0]));
      history.push('/dashboard');
    } else {
      yield put(
        authActions.fetchAdminSuccess(
          null,
          'Contact your administrator to request access',
        ),
      );
    }
  } catch (error) {
    yield put(authActions.fetchAdminFailure(error));
  }
}

function* createAdminSaga(userData, provider) {
  try {
    const admin = {
      email: userData.email,
      displayName: userData.displayName,
      provider,
      active: false,
      role: roles.OPERATOR,
      ...(userData.photoURL && { photoURL: userData.photoURL }),
      createdAt: serverTimestamp(),
    };
    yield call(rsf.firestore.setDocument, `users/${userData.uid}`, admin);
    yield put(
      authActions.createAdminSuccess(
        'Contact your administrator to request access',
      ),
    );
  } catch (error) {
    yield put(authActions.createAdminFailure(error));
  }
}

function* passwordChangeSaga({ password }) {
  try {
    yield call(rsf.auth.updatePassword, password);
    yield put(authActions.passwordChangeSuccess());
  } catch (error) {
    yield put(authActions.passwordChangeFailure(error));
  }
}

function* passwordForgetSaga({ email }) {
  try {
    yield call(rsf.auth.sendPasswordResetEmail, email);
    yield put(
      authActions.passwordForgetSuccess(
        'Reset link are sended to your mailbox, check there first',
      ),
    );
  } catch (error) {
    yield put(authActions.passwordForgetFailure(error));
  }
}

function* startImpersonationSaga({ userId }) {
  try {
    const currentAdmin = yield select((state) => state.Auth.admin);

    const userSnap = yield call(rsf.firestore.getDocument, `users/${userId}`);

    if (!userSnap.exists()) {
      throw new Error('User not found');
    }

    const data = userSnap.data();

    const impersonatedUser = {
      id: userId,
      ...data,
      createdAt: toDateFirebase(userSnap, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(userSnap, data, 'updatedAt').toDate(),
      }),
    };

    // Reset all store slices
    yield put(usersActions.resetState());
    yield put(dailyFacebookCampaignInsightsActions.resetState());
    yield put(dailyGoogleCampaignInsightsActions.resetState());
    yield put(leadDailyInsightsActions.resetState());
    yield put(leadsActions.resetState());
    yield put(peopleActions.resetState());
    yield put(audiencesActions.resetState());
    yield put(customersActions.resetState());
    yield put(dailyCustomerInsightsActions.resetState());
    yield put(acquisitionOrdersActions.resetState());
    yield put(monetizationOrdersActions.resetState());
    yield put(dailyMonetizationOrderInsightsActions.resetState());
    yield put(monetizationItemsActions.resetState());
    yield put(leadFieldsActions.resetState());
    yield put(leadFormsActions.resetState());
    yield put(aiAgentsActions.resetState());
    yield put(aiConversationsActions.resetState());
    yield put(aiConversationMonitorActions.resetState());
    yield put(aiSimulationActions.resetState());
    yield put(campaignsActions.resetState());
    yield put(dailyCampaignInsightsActions.resetState());
    yield put(surveysActions.resetState());
    yield put(surveyFormsActions.resetState());
    yield put(messageTemplatesActions.resetState());
    yield put(smsMessagesActions.resetState());
    yield put(pushNotificationsActions.resetState());
    yield put(callsActions.resetState());
    yield put(callsInsightsActions.resetState());
    yield put(nextCallActions.resetState());
    yield put(suppliersActions.resetState());
    yield put(dailySaleInsightsActions.resetState());
    yield put(dailyPublisherInsightsActions.resetState());
    yield put(dailyCountriesInsightsActions.resetState());

    // Set country to impersonated user's first country
    if (
      Array.isArray(impersonatedUser.countryIds) &&
      impersonatedUser.countryIds.length
    ) {
      yield put(dashboardActions.setCountry(impersonatedUser.countryIds[0]));
    }

    // Re-sync data
    yield put(usersActions.syncUsers());
    yield put(campaignsActions.syncCampaigns());
    yield put(acquisitionOrdersActions.syncAcquisitionOrders());
    yield put(monetizationOrdersActions.syncMonetizationOrders());

    yield put(
      authActions.startImpersonationSuccess(impersonatedUser, currentAdmin),
    );
    toastr.success(
      `Now impersonating ${impersonatedUser.displayName}`,
      'Impersonation Active',
    );
  } catch (error) {
    yield put(authActions.startImpersonationFailure(error));
    toastr.error(error.message, 'Impersonation Failed');
  }
}

function stopImpersonationSaga() {
  toastr.info('Impersonation stopped', '');
}

export default function* authSaga() {
  yield fork(syncAdminSaga);
  yield all([
    takeEvery(types.LOGIN_WITH_GOOGLE.REQUEST, loginWithGoogleSaga),
    takeEvery(types.LOGIN_WITH_EMAIL.REQUEST, loginWithEmailSaga),
    takeEvery(types.REGISTER_WITH_EMAIL.REQUEST, registerWithEmailSaga),
    takeEvery(types.REGISTER_WITH_EMAIL.SUCCESS, createAdminSaga),
    takeEvery(types.FETCH_ADMIN.REQUEST, fetchAdminSaga),
    takeEvery(types.LOGOUT.REQUEST, logoutSaga),
    takeEvery(types.PASSWORD_CHANGE.REQUEST, passwordChangeSaga),
    takeEvery(types.PASSWORD_FORGET.REQUEST, passwordForgetSaga),
    takeEvery(types.START_IMPERSONATION.REQUEST, startImpersonationSaga),
    takeEvery(types.STOP_IMPERSONATION, stopImpersonationSaga),
  ]);
}
